import type { AdminSearchResultItem } from '../../types';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { AutocompleteField, Body, IconButton, ListBox } from '@workramp/ui';
import ReactHtmlParser from 'react-html-parser';
import { MixPanel } from '~/common';
import { getIconPropsSearchResult } from '~/elc/features/learner-search/utils';
import { useAdminSearch } from '../../providers';
import { goToAdminAdvancedSearch } from '../../utils/goToAdminAdvancedSearch';
import { openAdminSearchResult } from '../../utils/urlUtils';
import { AdminSearchContentTypes } from '../../constants';
import { getSearchBarResultTitle, getSearchBarResultSubtitle } from '../../utils/adminSearchResultUtils';
import { AdminSearchItemStyledStack, AutocompleteFieldContainer } from './AdminSearchBar.style';

const { I18n } = window;

const contains = (textVal: string, inputVal: string) =>
  textVal.toLocaleLowerCase().includes(inputVal.toLocaleLowerCase());

const SEE_ALL_RESULTS_ID = 'see-all-results';

const DISPLAY_LIMIT = 10;

const NO_RESULTS_FOUND: AdminSearchResultItem = {
  id: 'no-result-found',
  name: I18n.t('js.common.item_picker.no_results'),
  snippetMatch: { title: null, subtitle: null, matched_words: [] },
  type: 'unknown',
  updatedDate: 0,
};

const SEARCHING: AdminSearchResultItem = {
  id: 'searching',
  name: I18n.t('js.common.item_picker.loading'),
  snippetMatch: { title: null, subtitle: null, matched_words: [] },
  type: 'unknown',
  updatedDate: 0,
};

const ERROR_OCCURRED: AdminSearchResultItem = {
  id: 'error-occurred',
  name: I18n.t('js.common.item_picker.error'),
  snippetMatch: { title: null, subtitle: null, matched_words: [] },
  type: 'unknown',
  updatedDate: 0,
};

type AdminSearchBarProps = {
  showMenu?: boolean;
};

export const AdminSearchBar = ({ showMenu = true }: AdminSearchBarProps) => {
  const { searchQuery, setSearchQuery, searchResults, isLoading, totalCount, isError, setCurrentItemInItemPane } =
    useAdminSearch();
  const [isFocused, setIsFocused] = useState(false);
  const [enterToAdvancedSearch, setEnterToAdvancedSearch] = useState(true);
  const TRANSLATED_SEARCH = I18n.t('js.admin.search.search');
  const { colors } = useTheme();

  let items: AdminSearchResultItem[] = [];
  let showSeeAllResults = false;

  if (!searchQuery.trim()) {
    items = [];
  } else if (isLoading) {
    items = [SEARCHING];
  } else if (isError) {
    items = [ERROR_OCCURRED];
  } else if (totalCount > 0) {
    showSeeAllResults = true;
    // Show only top 10 items in the search bar; full results will be shown on the Admin Search Page
    items = searchResults.slice(0, DISPLAY_LIMIT);
  } else if (searchQuery.trim()) {
    // Show "no results" only if user has typed something but we have no results
    items = [NO_RESULTS_FOUND];
  } else {
    items = [];
  }

  // Add "See all results (N)" as the last item if there are results
  const allItems = showSeeAllResults
    ? [
        ...items,
        {
          id: SEE_ALL_RESULTS_ID,
          name: I18n.t('js.admin.search.see_all_results', { count: totalCount }),
          snippetMatch: { title: null, subtitle: null, matched_words: [] },
          type: 'unknown',
          updatedDate: 0,
        } as AdminSearchResultItem,
      ]
    : items;

  return (
    <AutocompleteFieldContainer $isFocused={isFocused}>
      <AutocompleteField
        defaultFilter={contains}
        inputValue={searchQuery || ''}
        items={allItems}
        disabledKeys={[NO_RESULTS_FOUND.id, SEARCHING.id, ERROR_OCCURRED.id]}
        label={null}
        aria-label="Admin Search"
        leadingIconName="search:regular"
        data-testid="admin-search-autocomplete"
        onFocus={() => {
          setIsFocused(true);
          setEnterToAdvancedSearch(true);
        }}
        onBlur={() => setIsFocused(false)}
        placeholder={TRANSLATED_SEARCH}
        onInputChange={(v) => {
          setSearchQuery(v);
          setEnterToAdvancedSearch(true);
        }}
        onSelectionChange={(key) => {
          // If "See all results" is clicked, navigate to the admin advanced search page with the current search term
          if (key === SEE_ALL_RESULTS_ID) {
            goToAdminAdvancedSearch(searchQuery);
            return;
          }
          // If a search result is clicked, open admin page for that result item
          const selection = allItems.find(({ id }) => id === key);
          if (
            selection &&
            selection.id !== NO_RESULTS_FOUND.id &&
            selection.id !== SEARCHING.id &&
            selection.id !== ERROR_OCCURRED.id
          ) {
            MixPanel.track('ADMIN_SEARCH_BAR_CLICKS_RESULT', {
              title: selection.name,
              type: selection.type,
              id: selection.id,
            });
            if (selection.type === AdminSearchContentTypes.ITEM_FOLDER) {
              setCurrentItemInItemPane(selection);
            } else {
              openAdminSearchResult(selection);
            }
          }
        }}
        allowsCustomValue
        onKeyDown={(e) => {
          if (e.key === 'Enter' && searchQuery && enterToAdvancedSearch) {
            goToAdminAdvancedSearch(searchQuery);
          }
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            setEnterToAdvancedSearch(false);
          }
        }}
        menuTrigger={showMenu ? 'focus' : 'manual'}
        showTrailingButton={!!searchQuery.trim()}
        trailingButtonEl={
          <IconButton
            aria-label={I18n.t('js.admin.search.clear')}
            color="secondary"
            iconName="x-circle:solid"
            label={I18n.t('js.admin.search.clear')}
            onPress={() => setSearchQuery('')}
            size="lg"
            variant="text"
          />
        }
      >
        {(item) => {
          if (item.id === NO_RESULTS_FOUND.id || item.id === SEARCHING.id || item.id === ERROR_OCCURRED.id) {
            return <ListBox.Item key={item.id}>{item.name}</ListBox.Item>;
          }
          if (item.id === SEE_ALL_RESULTS_ID) {
            return (
              <ListBox.Section key={`${item.id}-section`} aria-label={I18n.t('js.admin.search.see_all_results_label')}>
                <ListBox.Item key={item.id} textValue={searchQuery || ''} alignItem="center" color="primary">
                  <Body level="md" color={colors.primary[600]} style={{ fontWeight: 500 }}>
                    {item.name}
                  </Body>
                </ListBox.Item>
              </ListBox.Section>
            );
          }
          const { id, type, contentSource } = item;
          const title = getSearchBarResultTitle(item);
          const subtitle = getSearchBarResultSubtitle(item);
          const iconProps = getIconPropsSearchResult({
            type,
            subType: contentSource,
            colors,
          });

          return (
            <ListBox.Item
              key={id}
              iconName={iconProps.name}
              iconColor={iconProps.color}
              textValue={item.name}
              alignItem="start"
            >
              <AdminSearchItemStyledStack size={0}>
                <Body level="md" maxLines={1}>
                  {ReactHtmlParser(title)}
                </Body>
                {subtitle && (
                  <Body level="sm" color={colors.on[600]} maxLines={1}>
                    {ReactHtmlParser(subtitle)}
                  </Body>
                )}
              </AdminSearchItemStyledStack>
            </ListBox.Item>
          );
        }}
      </AutocompleteField>
    </AutocompleteFieldContainer>
  );
};
